import React, { useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { useIsMobile } from '../ui/use-mobile';
import {
  bookingBrandFontFamilyVar,
  bookingBrandInfoChromeStyles,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import { openReservationMobilePass } from '../../services/mobile-passes';
import { WalletAddButtons } from './WalletAddButtons';

type ActionBannerStyle = { bg: string; border: string; text: string; icon: string };

interface ReservationConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservationDetails?: {
    teeTime: string;
    course: string;
    date: string;
    players: number;
    price: number;
    reservationId?: string;
    mobilePassAccessToken?: string;
    successSubtitle?: string;
    staffMessageNote?: { text: string; variant: 'success' | 'warning' };
  };
  /** Bookers with multi-player rounds can mint/copy a partner invite link (requires sign-in). */
  partnerInvite?: {
    inviteUrl: string | null;
    loading: boolean;
    error: string | null;
    canInvite: boolean;
    onMint: () => void;
  };
  brandColors?: {
    fontFamily: string;
    textColor: string;
    buttonColor: string;
    buttonTextColor: string;
    /** Text on primary surfaces (primary CTAs) — admin “Text on primary”. */
    primaryTextColor?: string;
    /** Club primary — drives brand wash when set (falls back to buttonColor). */
    primaryColor?: string;
  };
}

export const ReservationConfirmationModal: React.FC<ReservationConfirmationModalProps> = ({
  isOpen,
  onClose,
  reservationDetails,
  partnerInvite,
  brandColors: brandColorsProp,
}) => {
  const isMobile = useIsMobile();
  const tebox = useBookingTebox();
  const animateOnceRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      animateOnceRef.current = true;
    } else {
      animateOnceRef.current = false;
    }
  }, [isOpen]);

  const shouldAnimate = animateOnceRef.current && isOpen;
  const floatingToolbar = tebox.components.floatingToolbar;
  const bannerTypography = floatingToolbar.typography.banner;
  const actionBannerStyles = floatingToolbar.actionBannerStyles as Record<string, ActionBannerStyle>;

  const brandColors = brandColorsProp ?? {
    fontFamily: 'Inter',
    textColor: tebox.colors.textPrimary,
    buttonColor: '#000000',
    buttonTextColor: '#FFFFFF',
    primaryTextColor: '#FFFFFF',
  };

  const textOnPrimary =
    brandColors.primaryTextColor ?? brandColors.buttonTextColor ?? '#FFFFFF';

  const reservationDetailsChrome = useMemo(
    () =>
      bookingBrandInfoChromeStyles({
        primaryColor: brandColors.primaryColor,
        buttonColor: brandColors.buttonColor,
      }),
    [brandColors.primaryColor, brandColors.buttonColor],
  );

  /** Matches checkout adjusted-price accent — icon emphasis on brand wash. */
  const confirmationBrandAccentInk = useMemo(
    () =>
      `color-mix(in srgb, ${(brandColors.primaryColor?.trim() || brandColors.buttonColor?.trim() || '#000000')} 46%, black)`,
    [brandColors.primaryColor, brandColors.buttonColor],
  );

  if (!isOpen) return null;

  if (typeof document === 'undefined') return null;

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  /** Portal to body so `position: fixed` is viewport-relative (checkout shell uses `transform`, which breaks fixed). */
  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: isMobile
          ? `max(${teboxDesignSystem.spacing.md}, env(safe-area-inset-top, 0px)) ${teboxDesignSystem.spacing.md} max(${teboxDesignSystem.spacing.md}, env(safe-area-inset-bottom, 0px))`
          : teboxDesignSystem.spacing.xl,
        animation: 'fadeIn 0.3s ease-out',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
      }}
      onClick={onClose}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideInUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes slideInLeft {
            from { 
              opacity: 0;
              transform: translateX(-20px);
            }
            to { 
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .confirmation-modal {
            animation: ${shouldAnimate && !isMobile ? 'slideInUp 0.5s ease-out' : 'none'};
          }
          
          .detail-item {
            animation: ${shouldAnimate && !isMobile ? 'slideInLeft 0.4s ease-out forwards' : 'none'};
            opacity: ${shouldAnimate && !isMobile ? 0 : 1};
          }
          
          .detail-item:nth-child(1) { animation-delay: 0.1s; }
          .detail-item:nth-child(2) { animation-delay: 0.2s; }
          .detail-item:nth-child(3) { animation-delay: 0.3s; }
          .detail-item:nth-child(4) { animation-delay: 0.4s; }
          .detail-item:nth-child(5) { animation-delay: 0.5s; }
        `}
      </style>
      
      <div 
        className="confirmation-modal"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: 'var(--brand-border-radius, 6px)',
          padding: isMobile ? teboxDesignSystem.spacing.lg : '40px',
          maxWidth: isMobile ? '100%' : '800px',
          width: '100%',
          margin: 'auto',
          flexShrink: 0,
          maxHeight: isMobile
            ? 'min(94dvh, calc(100dvh - 24px))'
            : 'min(92vh, calc(100vh - 48px))',
          overflowX: 'hidden',
          overflowY: 'auto',
          boxShadow: 'none',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
        }} 
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            marginBottom: '24px',
          }}
        >
          <h2
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '500',
              fontSize: isMobile
                ? teboxDesignSystem.typography.h4.fontSize
                : teboxDesignSystem.typography.h3.fontSize,
              lineHeight: isMobile ? '26px' : '32px',
              letterSpacing: '-1px',
              color: brandColors.textColor,
              margin: 0,
              textAlign: 'left',
            }}
          >
            Reservation Confirmed
          </h2>
        </div>

        {/* Success message — subtitle or consolidated banner */}
        <div
          style={{
            textAlign: 'left',
            marginBottom: '32px',
            position: 'relative',
            zIndex: 1
          }}
        >
          {reservationDetails?.successSubtitle ? (
            <div
              role="status"
              aria-live="polite"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: teboxDesignSystem.spacing.sm,
                padding: '16px',
                margin: 0,
                maxWidth: '100%',
                textAlign: 'left',
                borderRadius: 'var(--brand-border-radius, 6px)',
                boxSizing: 'border-box',
                ...reservationDetailsChrome,
                fontFamily: bookingBrandFontFamilyVar,
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: teboxDesignSystem.typography.body.fontSize,
                color: tebox.colors.textPrimary,
                lineHeight: 1.5,
              }}
            >
              <Check
                size={20}
                strokeWidth={2}
                color={confirmationBrandAccentInk}
                style={{ flexShrink: 0, marginTop: 2 }}
                aria-hidden
              />
              <span style={{ flex: 1, minWidth: 0 }}>{reservationDetails.successSubtitle}</span>
            </div>
          ) : (
            <p
              style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: teboxDesignSystem.typography.body.fontSize,
                lineHeight: '22px',
                color: brandColors.textColor,
                margin: 0,
                textAlign: 'left',
              }}
            >
              Your tee time has been successfully reserved. See details below.
            </p>
          )}
          {reservationDetails?.reservationId ? (
            <p
              style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontStyle: 'normal',
                fontWeight: '500',
                fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                lineHeight: '20px',
                color: tebox.colors.textSecondary,
                margin: '12px 0 0 0',
                wordBreak: 'break-all'
              }}
            >
              Confirmation reference:{' '}
              <span style={{ color: brandColors.textColor, fontWeight: 500 }}>{reservationDetails.reservationId}</span>
            </p>
          ) : null}
          {reservationDetails?.staffMessageNote
            ? (() => {
                const staffStyle =
                  reservationDetails.staffMessageNote!.variant === 'success'
                    ? actionBannerStyles.reservation
                    : actionBannerStyles.error;
                return (
                  <div
                    role="status"
                    aria-live="polite"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '8px 12px 8px 16px',
                      marginTop: '16px',
                      textAlign: 'left',
                      backgroundColor: staffStyle.bg,
                      border: `1px solid ${staffStyle.border}`,
                      borderRadius: tebox.radius.none,
                      boxShadow: 'none',
                      fontSize: bannerTypography.fontSize,
                      fontWeight: bannerTypography.fontWeight,
                      fontFamily: bannerTypography.fontFamily,
                      color: staffStyle.text,
                      lineHeight: 1.45
                    }}
                  >
                    <Check
                      size={18}
                      strokeWidth={1.5}
                      color={staffStyle.icon}
                      style={{ flexShrink: 0, marginTop: 1 }}
                      aria-hidden
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>{reservationDetails.staffMessageNote!.text}</span>
                  </div>
                );
              })()
            : null}
          {partnerInvite?.canInvite ? (
            <div
              style={{
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: 'var(--brand-border-radius, 6px)',
                border: `1px solid ${tebox.colors.border}`,
                backgroundColor: tebox.colors.bgSecondary,
                textAlign: 'left',
              }}
            >
              <p
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontWeight: 500,
                  fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                  color: brandColors.textColor,
                  margin: '0 0 8px 0',
                }}
              >
                Invite playing partners
              </p>
              <p
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontSize: teboxDesignSystem.typography.caption.fontSize,
                  color: tebox.colors.textSecondary,
                  margin: '0 0 10px 0',
                  lineHeight: 1.45,
                }}
              >
                Create a link to share with your group. They can sign in and join an open player spot.
                Creating a new link replaces the previous one.
              </p>
              {partnerInvite.error ? (
                <p
                  role="alert"
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontSize: teboxDesignSystem.typography.caption.fontSize,
                    color: '#b91c1c',
                    margin: '0 0 8px 0',
                  }}
                >
                  {partnerInvite.error}
                </p>
              ) : null}
              {partnerInvite.inviteUrl ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: teboxDesignSystem.spacing.sm,
                    marginBottom: teboxDesignSystem.spacing.sm,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontSize: teboxDesignSystem.typography.caption.fontSize,
                      fontWeight: 500,
                      color: tebox.colors.textSecondary,
                      letterSpacing: '0.01em',
                    }}
                  >
                    Your invite link
                  </span>
                  <div
                    title={partnerInvite.inviteUrl}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      borderRadius: 'var(--brand-border-radius, 6px)',
                      border: `1px solid ${tebox.colors.borderField}`,
                      backgroundColor: tebox.colors.bgPrimary,
                      padding: `${teboxDesignSystem.spacing.sm} ${teboxDesignSystem.spacing.md}`,
                      overflowX: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarGutter: 'stable',
                    }}
                  >
                    <code
                      style={{
                        display: 'block',
                        margin: 0,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
                        fontSize: teboxDesignSystem.typography.caption.fontSize,
                        color: brandColors.textColor,
                        whiteSpace: 'nowrap',
                        lineHeight: 1.45,
                        userSelect: 'all',
                      }}
                    >
                      {partnerInvite.inviteUrl}
                    </code>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(partnerInvite.inviteUrl!);
                    }}
                    aria-label="Copy invite link to clipboard"
                    style={{
                      alignSelf: 'flex-start',
                      padding: '8px 14px',
                      fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                      fontWeight: 500,
                      color: textOnPrimary,
                      backgroundColor: brandColors.buttonColor,
                      border: 'none',
                      borderRadius: 'var(--brand-border-radius, 6px)',
                      cursor: 'pointer',
                      fontFamily: bookingBrandFontFamilyVar,
                    }}
                  >
                    Copy link
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={partnerInvite.onMint}
                disabled={partnerInvite.loading}
                style={{
                  padding: '10px 18px',
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  fontWeight: 500,
                  color: brandColors.textColor,
                  backgroundColor: tebox.colors.bgPrimary,
                  border: `1px solid ${tebox.colors.border}`,
                  borderRadius: 'var(--brand-border-radius, 6px)',
                  cursor: partnerInvite.loading ? 'wait' : 'pointer',
                  fontFamily: bookingBrandFontFamilyVar,
                  opacity: partnerInvite.loading ? 0.75 : 1,
                }}
              >
                {partnerInvite.loading
                  ? 'Creating link…'
                  : partnerInvite.inviteUrl
                    ? 'Generate new link'
                    : 'Create invite link'}
              </button>
            </div>
          ) : null}
        </div>

        {/* Reservation Details */}
        {reservationDetails && (
          <div
            style={{
              borderRadius: 'var(--brand-border-radius, 6px)',
              padding: '16px',
              marginBottom: '32px',
              boxSizing: 'border-box',
              ...reservationDetailsChrome,
            }}
          >
            <h3 style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '500',
              fontSize: teboxDesignSystem.typography.h4.fontSize,
              lineHeight: '24px',
              color: brandColors.textColor,
              margin: '0 0 16px 0'
            }}>
              Reservation Details
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '14px' }}>
              {(
                [
                  ['Course:', reservationDetails.course],
                  ['Date:', reservationDetails.date],
                  ['Time:', formatTime(reservationDetails.teeTime)],
                  ['Players:', String(reservationDetails.players)],
                ] as const
              ).map(([label, value]) => (
                <div
                  key={label}
                  className="detail-item"
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: isMobile ? 'flex-start' : 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: isMobile ? '4px' : 0,
                  }}
                >
                  <span style={{ fontFamily: bookingBrandFontFamilyVar, fontWeight: '500', fontSize: teboxDesignSystem.typography.body.fontSize, color: brandColors.textColor }}>{label}</span>
                  <span style={{ fontFamily: bookingBrandFontFamilyVar, fontWeight: '500', fontSize: teboxDesignSystem.typography.body.fontSize, color: brandColors.textColor, textAlign: isMobile ? 'left' : 'right' }}>{value}</span>
                </div>
              ))}

              <div className="detail-item" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '4px' : 0, paddingTop: '8px', borderTop: `1px solid ${tebox.colors.border}` }}>
                <span style={{ fontFamily: bookingBrandFontFamilyVar, fontWeight: '500', fontSize: teboxDesignSystem.typography.body.fontSize, color: brandColors.textColor }}>Total:</span>
                <span style={{ fontFamily: bookingBrandFontFamilyVar, fontWeight: '500', fontSize: teboxDesignSystem.typography.body.fontSize, color: brandColors.textColor }}>${Number(reservationDetails.price).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {reservationDetails?.reservationId ? (
          <div
            style={{
              marginBottom: isMobile ? '20px' : '24px',
              padding: isMobile ? '14px 16px' : '16px 18px',
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: 'var(--brand-border-radius, 6px)',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                margin: '0 0 8px',
                fontFamily: bookingBrandFontFamilyVar,
                fontSize: teboxDesignSystem.typography.caption.fontSize,
                fontWeight: 500,
                color: brandColors.textColor,
                opacity: 0.7,
              }}
            >
              Wallet
            </p>
            <WalletAddButtons
              embedded
              onAppleClick={() =>
                openReservationMobilePass(
                  reservationDetails.reservationId!,
                  'apple',
                  reservationDetails.mobilePassAccessToken,
                )
              }
              onGoogleClick={() =>
                openReservationMobilePass(
                  reservationDetails.reservationId!,
                  'google',
                  reservationDetails.mobilePassAccessToken,
                )
              }
            />
          </div>
        ) : null}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: isMobile ? '10px' : '14px',
          justifyContent: 'center',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: isMobile ? '14px 20px' : '12px 24px',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              fontWeight: '500',
              color: textOnPrimary,
              backgroundColor: brandColors.buttonColor,
              border: 'none',
              borderRadius: 'var(--brand-border-radius, 6px)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: bookingBrandFontFamilyVar,
              minWidth: isMobile ? '100%' : '120px',
              width: isMobile ? '100%' : undefined,
            }}
            onMouseEnter={(e) => {
              // Darken button color slightly on hover
              const rgb = brandColors.buttonColor.match(/\d+/g);
              if (rgb) {
                const r = Math.max(0, parseInt(rgb[0]) - 20);
                const g = Math.max(0, parseInt(rgb[1]) - 20);
                const b = Math.max(0, parseInt(rgb[2]) - 20);
                e.currentTarget.style.backgroundColor = `rgb(${r}, ${g}, ${b})`;
              }
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = brandColors.buttonColor;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Continue Booking
          </button>
          
          <button
            onClick={onClose}
            style={{
              padding: isMobile ? '14px 20px' : '12px 24px',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              fontWeight: '500',
              color: brandColors.textColor,
              backgroundColor: tebox.colors.bgSecondary,
              border: `1px solid ${teboxDesignSystem.colors.border.light}`,
              borderRadius: 'var(--brand-border-radius, 6px)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: bookingBrandFontFamilyVar,
              minWidth: isMobile ? '100%' : '120px',
              width: isMobile ? '100%' : undefined,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tebox.colors.border;
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = tebox.colors.bgSecondary;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Close
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};
